/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  FormControl,
  Stack,
  TextField,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Grid,
  Typography,
  GridSize,
  Tooltip,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import SelectDateRange from 'src/components/SelectDateRange';
import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  initialState,
  reducerUpdateAdvanceClear,
  reducerUpdateAdvanceSearch,
  reducerUpdateAdvanceSearchIncludeKeywords
} from 'src/features/advanceSearchEcommerce';
import AdvanceSearchEcommerce from 'src/components/AdvanceSearchEcommerce';
import ClearIcon from '@mui/icons-material/Clear';
import changeObjectToQuery from 'src/utils/changeObjectToQuery';
import FilterListIcon from '@mui/icons-material/FilterList';
import TooltipCustomize from 'src/components/TooltipCustomize';
import RefreshIcon from '@mui/icons-material/Refresh';
import { IGranularity, ITableAtribute } from 'src/models/general';
import {
  reducerUpdateIsSelectedAllPrimary,
  reducerUpdateStreamEcommerceSelected
} from 'src/features/ecommerceStream';
import useObjectManagement from 'src/services/useObjectManagement';
import { concatUniqueObjectsById } from 'src/utils/concatUniqueObjectsById';
import { reducerUpdateObjectListByObjectType } from 'src/features/objectManagement';
import { reducerUpdateAnalyticArgumentObjectIdList } from 'src/features/analyticArguments';
import { useAlert } from 'src/hooks/useAlert';
import useEcommerce from 'src/services/useEcommerce';
import FilterBrand from 'src/components/FilterEcommerce/FilterBrand';
import FilterSubBrand from 'src/components/FilterEcommerce/FilterSubBrand';
import FilterProduct from 'src/components/FilterEcommerce/FilterProduct';
import FilterLabel from 'src/components/FilterEcommerce/FilterLabel';
import FilterSentiment from 'src/components/FilterEcommerce/FilterSentiment';
import FilterRating from 'src/components/FilterEcommerce/FilterRating';
import { Clear, RestoreFromTrash } from '@mui/icons-material';
import FilterProductStage from 'src/components/FilterEcommerce/FilterProductStage';
import SelectDateRangeRetail from 'src/components/Retail/SelectDateRangeRetail';

const CustomBox = styled(Box)(
  ({ theme }) => `
    // width: calc(100vw - ${theme.sidebar.width} - ${theme.spacing(10)});
    margin: ${theme.spacing(2, 0)};
    // background: #fbfbfb;
    // position: absolute;
`
);

const FilterBox = styled(Box)(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`
);

const ChipCustom = styled(Chip)(
  ({ theme }) => `
    background-color: ${theme.palette.primary.light};
    border-radius: ${theme.spacing(1)};
    padding: 10px ${theme.spacing(2)};
    height: 29px;
    width: 100px;
    .MuiChip-label {
        color: #0A789D;
        font-size: 14px;
        line-height: 130%;
        margin: 0 4px;
    }
    .MuiChip-deleteIcon {
        font-size: 14px;
        color: #0A789D;
    }
  `
);

function BottomHeader() {
  const dispatch = useAppDispatch();
  const advanceSearch = useAppSelector(
    (state) => state.storeAdvancedSearchEcommerce
  );
  const {
    streamsCommentingToPost,
    selectedOriginalToPost,
    searchOriginalToPost,
    selectedBrand,
    selectedSubBrand,
    selectedLabel,
    selectedProductStages,
    ...rest
  } = advanceSearch;

  const query = changeObjectToQuery(rest);
  const [localSearch, setLocalSearch] = useState<string>(query);
  const advanceSearchKeys = query
    ? query.split(';').slice(0, query.split(';').length - 1)
    : [];

  const handleClear = () => {
    setLocalSearch('');
    dispatch(reducerUpdateAdvanceClear(initialState));
  };

  const handleSearch = () => {
    dispatch(reducerUpdateAdvanceSearchIncludeKeywords([localSearch]));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') handleSearch();
  };

  useEffect(() => {
    setLocalSearch(query);
  }, [advanceSearch]);

  const theme = useTheme();

  const [open, setOpen] = useState<boolean>(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const projectId = useAppSelector((state) => state.users.project.id);

  const { objectIdList } = useAppSelector((state) => state.analyticArguments);

  const ecommerceObjectList = useAppSelector(
    (state) => state.objectManagement.ecommerceObjectList
  );

  const {
    groupSelectedById: selectedGroupId,
    // groupObject: prevGroupObject,
    selectedGroups
  } = useAppSelector((state) => state.objectManagement);

  const { handleClickAlert } = useAlert();
  const { useGetEcommerceObjectList, useGetEcommerceGroups } = useEcommerce();

  const [getObjectList] = useGetEcommerceObjectList();
  const { refetch: getGroupObject } = useGetEcommerceGroups({
    skip: true
  });

  const handleDeleteAllFilter = () => {
    dispatch(reducerUpdateAdvanceSearch(initialState));
  };

  const handleDeleteFilter = (item: string) => {
    const splitFilter = item.split(':');
    const deletedFilter = splitFilter[0].trim();
    dispatch(
      reducerUpdateAdvanceSearch({
        ...advanceSearch,
        [deletedFilter]: initialState[deletedFilter]
      })
    );
  };

  const handleRefresh = () => {
    dispatch(reducerUpdateStreamEcommerceSelected([]));
    dispatch(reducerUpdateIsSelectedAllPrimary(false));
    Promise.all([
      getObjectList({
        variables: {
          projectId
        }
      }),
      getGroupObject({ projectId: projectId, templateName: 'content' })
    ])
      .then((responses) => {
        // if a group is selected
        if (selectedGroupId !== '_default' || selectedGroups.length > 0) {
          const newGroupObjectList = responses[1].data;
          const prevSelectedGroups = selectedGroups;

          const prevSelectedGroupIds = prevSelectedGroups
            .map((group) => group.objectList.map((group) => group.id))
            .flat();

          const newSelectedGroups =
            newGroupObjectList?.objectManagement_getObjectGroupList?.filter(
              (group) =>
                prevSelectedGroups.some(
                  (prevGroup) => prevGroup.id === group.id
                )
            );

          const allObjectsFromGroups = newSelectedGroups.flatMap(
            (group) => group.objectList
          );

          // semua object yang ada di active object + object baru di grup
          const finalActiveObjects = allObjectsFromGroups.filter(
            (object) =>
              objectIdList.includes(object.id) ||
              !prevSelectedGroupIds.includes(object.id)
          );
          dispatch(reducerUpdateObjectListByObjectType(finalActiveObjects));
          dispatch(
            reducerUpdateAnalyticArgumentObjectIdList(
              finalActiveObjects.map((object) => object.id)
            )
          );
          // if no group selected
        } else {
          const newObjectList = responses[0].data.ecommerce_getObject;
          // get only new objects
          const newObjects = newObjectList.filter((object) => {
            const prevObjectListIds = ecommerceObjectList.map(
              (object) => object.id
            );
            return !prevObjectListIds.includes(object.id);
          });

          const selectedObjects = newObjectList.filter((object) =>
            objectIdList.includes(object.id)
          );
          const finalActiveObjects = concatUniqueObjectsById(
            selectedObjects,
            newObjects
          );

          dispatch(reducerUpdateObjectListByObjectType(finalActiveObjects));
          dispatch(
            reducerUpdateAnalyticArgumentObjectIdList(
              finalActiveObjects.map((object) => object.id)
            )
          );
        }
      })
      .catch((error) =>
        handleClickAlert({
          vertical: 'top',
          horizontal: 'center',
          message: "Couldn't fetch data, please try again.",
          severity: 'error'
        })
      );
  };

  useEffect(() => {
    setLocalSearch(query);
  }, [advanceSearch]);

  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <CustomBox>
      <FilterBox
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={{ xs: 1 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent={{ xs: 'flex-start', lg: 'space-between' }}
        sx={{ width: '100%' }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            maxWidth={isLargeScreen ? '400px' : 'unset'}
          >
            <TextField
              size="small"
              InputProps={{
                startAdornment: (
                  <IconButton
                    size="small"
                    onClick={handleSearch}
                    sx={{ backgroundColor: 'white' }}
                  >
                    <SearchTwoToneIcon />
                  </IconButton>
                ),
                endAdornment: localSearch ? (
                  <IconButton size="small" onClick={handleClear}>
                    <ClearIcon />
                  </IconButton>
                ) : null,
                sx: { borderRadius: '16px', backgroundColor: 'white' }
              }}
              placeholder="Search stream"
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              fullWidth
            />
            <SelectDateRangeRetail
              selectProps={{
                sx: {
                  borderRadius: '16px',
                  width: '200px'
                }
              }}
            />
          </Stack>
          <Stack
            direction="row"
            justifyContent={isLargeScreen ? 'unset' : 'space-between'}
            spacing={1}
            alignItems="center"
          >
            <TooltipCustomize title="Advance search" placement="top-start">
              <Button
                variant="contained"
                sx={{
                  borderRadius: '16px',
                  height: theme.spacing(5),
                  minWidth: theme.spacing(6),
                  background: theme.palette.primary.main
                }}
                onClick={() => handleClickOpen()}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FilterListIcon htmlColor="#FFFFFF" />
                  <Typography color="white" fontWeight={700}>
                    Filter
                  </Typography>
                </Stack>
              </Button>
            </TooltipCustomize>
            {open && (
              <AdvanceSearchEcommerce open={open} onClose={handleClose} />
            )}
          </Stack>
          <Stack
            direction="row"
            overflow="auto"
            maxWidth={400}
            spacing={1}
            sx={{
              scrollbarWidth: 'thin'
            }}
          >
            {advanceSearchKeys
              ? advanceSearchKeys.map((item) => {
                  return (
                    <Tooltip title={item}>
                      <ChipCustom
                        variant="filled"
                        deleteIcon={<ClearIcon />}
                        onDelete={() => handleDeleteFilter(item)}
                        label={item}
                        size="small"
                      />
                    </Tooltip>
                  );
                })
              : null}
          </Stack>
          {advanceSearchKeys.length > 0 ? (
            <Stack
              direction="row"
              alignItems="center"
              sx={{ cursor: 'pointer' }}
              onClick={handleDeleteAllFilter}
            >
              <RestoreFromTrash fontSize="small" />
              <Typography fontSize="14px">Clear Filters</Typography>
            </Stack>
          ) : null}
        </Stack>
        <TooltipCustomize title="Refresh all metric" placement="top-start">
          <Button
            variant="contained"
            onClick={handleRefresh}
            sx={{
              borderRadius: '6px',
              height: theme.spacing(5),
              minWidth: theme.spacing(6),
              background: theme.palette.primary.main
            }}
          >
            <RefreshIcon />
          </Button>
        </TooltipCustomize>
      </FilterBox>
      <Grid container mt={2} spacing={1}>
        <Grid item lg={(12 / 7) as GridSize}>
          <FilterBrand />
        </Grid>
        <Grid item lg={(12 / 7) as GridSize}>
          <FilterSubBrand />
        </Grid>
        <Grid item lg={(12 / 7) as GridSize}>
          <FilterProductStage />
        </Grid>
        <Grid item lg={(12 / 7) as GridSize}>
          <FilterProduct />
        </Grid>
        <Grid item lg={(12 / 7) as GridSize}>
          <FilterLabel />
        </Grid>
        <Grid item lg={(12 / 7) as GridSize}>
          <FilterSentiment />
        </Grid>
        <Grid item lg={(12 / 7) as GridSize}>
          <FilterRating />
        </Grid>
      </Grid>
    </CustomBox>
  );
}

export default BottomHeader;
